import { useMemo } from 'react'

import type { UseFiltersPayload } from '@domain-group/react-hooks'
import { useModals, useSetState, useShowModal } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import { generateUrls } from '@hooks/useCampaignQuote'

import CreateCampaignContactModal from '@modals/CreateCampaignContactModal'
import CreateFinanceApplicationModal from '@modals/CreateFinanceApplicationModal'
import CreateOrEditCampaignContributionModal from '@modals/CreateOrEditCampaignContributionModal'
import CreateOrEditContactModal from '@modals/CreateOrEditContactModal'
import EditCampaignDatesModal from '@modals/EditCampaignDatesModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import NewCampaignQuoteModal from '@modals/NewCampaignQuoteModal'
import SelectEmailRecipientModal from '@modals/SelectEmailRecipientModal'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import type {
  ActivityModel,
  AgencyModel,
  CampaignContactModel,
  CampaignModel,
  CampaignQuoteModel,
  ContactModel,
  FinanceApplicationModel,
} from '@models/types'

import PageContent from './components/PageContent'

import useEditQuoteRedirect from './hooks/useEditQuoteRedirect'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateCampaignContactModal: () => void,
    showCreateFinanceApplicationModal: () => void,
    showCreateOrEditCampaignContributionModal: () => void,
    showCreateOrEditContactModal: () => void,
    showEditCampaignDatesModal: () => void,
    showManageActivityModal: () => void,
    showNewCampaignQuoteModal: () => void,
    showSelectEmailRecipientModal: () => void,
  },
}

const defaultState = {
  showCreateCampaignContactModal: false,
  showCreateFinanceApplicationModal: false,
  showCreateOrEditCampaignContributionModal: false,
  showCreateOrEditContactModal: false,
  showEditCampaignDatesModal: false,
  showManageActivityModal: false,
  showNewCampaignQuoteModal: false,
  showSelectEmailRecipientModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateCampaignContactModal: {
      closeModal: () => setState({ showCreateCampaignContactModal: false }),
      createCampaignContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateFinanceApplicationModal: {
      closeModal: () => setState({ showCreateFinanceApplicationModal: false }),
      createFinanceApplication: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignContributionModal: {
      closeModal: () => setState({ showCreateOrEditCampaignContributionModal: false }),
      createCampaignContribution: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignContribution: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditContactModal: {
      closeModal: () => setState({ showCreateOrEditContactModal: false }),
      createContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createContactUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      shareMobileLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditCampaignDatesModal: {
      closeModal: () => setState({ showEditCampaignDatesModal: false }),
      updateCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    NewCampaignQuoteModal: {
      afterAction: ({ response }: { response: { success: boolean, data: CampaignQuoteModel }}) => {
        if (!response.success) return

        const campaignQuote = response.data

        setState({ showNewCampaignQuoteModal: false })

        const { editCampaignQuoteUrl } = generateUrls(campaignQuote, {
          prefixRoute: `campaigns/${campaignQuote.campaign_id}/edit`,
        })

        window.location.href = editCampaignQuoteUrl
      },
      closeModal: () => setState({ showNewCampaignQuoteModal: false }),
      createCampaignQuote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createQuoteFromExisting: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createQuoteFromTemplate: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    SelectEmailRecipientModal: {
      closeModal: () => setState({ showSelectEmailRecipientModal: false }),
      sendQuote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

type ListProps = {
  agency: AgencyModel,
  campaign: CampaignModel,
}

const List = (props: ListProps) => {
  const { agency, campaign } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateCampaignContactModal,
    showCreateFinanceApplicationModal,
    showCreateOrEditCampaignContributionModal,
    showCreateOrEditContactModal,
    showEditCampaignDatesModal,
    showManageActivityModal,
    showNewCampaignQuoteModal,
    showSelectEmailRecipientModal,
  } = state

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData }, topModalKey } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateCampaignContactModal: (payload: { campaignContact: CampaignContactModel }) => {
        setModalData('CreateCampaignContactModal', payload)
        setState({ showCreateCampaignContactModal: true })
      },
      showCreateFinanceApplicationModal: (payload: { financeApplication: FinanceApplicationModel }) => {
        setModalData('CreateFinanceApplicationModal', payload)
        setState({ showCreateFinanceApplicationModal: true })
      },
      showCreateOrEditCampaignContributionModal: (payload: object) => {
        setModalData('CreateOrEditCampaignContributionModal', payload)
        setState({ showCreateOrEditCampaignContributionModal: true })
      },
      showCreateOrEditContactModal: (payload: { contact: ContactModel }) => {
        setModalData('CreateOrEditContactModal', payload)
        setState({ showCreateOrEditContactModal: true })
      },
      showEditCampaignDatesModal: (payload: { campaign: CampaignModel }) => {
        setModalData('EditCampaignDatesModal', payload)
        setState({ showEditCampaignDatesModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showNewCampaignQuoteModal: () => {
        setState({ showNewCampaignQuoteModal: true })
      },
      showSelectEmailRecipientModal: (payload: { campaignQuote: CampaignQuoteModel }) => {
        setModalData('SelectEmailRecipientModal', payload)
        setState({ showSelectEmailRecipientModal: true })
      },
    },
  }), [topModalKey]) // modalContext dependency required if layering modals

  useShowModal({
    modalKey: 'NewCampaignQuoteModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  useShowModal({
    modalKey: 'CreateCampaignContactModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  useShowModal({
    modalKey: 'EditCampaignDatesModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  useEditQuoteRedirect()

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent
          campaign={campaign}
        />

        <CreateCampaignContactModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateCampaignContactModal', setState)}
          showModal={showCreateCampaignContactModal}
        />

        <CreateFinanceApplicationModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateFinanceApplicationModal', setState)}
          showModal={showCreateFinanceApplicationModal}
        />

        <CreateOrEditCampaignContributionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignContributionModal', setState)}
          showModal={showCreateOrEditCampaignContributionModal}
        />

        <CreateOrEditContactModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditContactModal', setState)}
          showModal={showCreateOrEditContactModal}
        />

        <EditCampaignDatesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditCampaignDatesModal', setState)}
          showModal={showEditCampaignDatesModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <NewCampaignQuoteModal
          // @ts-expect-error TS2322
          callbacks={callbacks('NewCampaignQuoteModal', setState)}
          campaignQuote={{ agency_id: campaign.agency_id, campaign_id: campaign.id }}
          requestOptions={defaultRequestOptions.campaignQuote}
          showModal={showNewCampaignQuoteModal}
        />

        <SelectEmailRecipientModal
          // @ts-expect-error TS2322
          callbacks={callbacks('SelectEmailRecipientModal', setState)}
          showModal={showSelectEmailRecipientModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
